import isEqual from 'lodash/isEqual';
import { Document, isMap, isScalar, parseDocument, stringify } from 'yaml';
import type { Pair, YAMLMap } from 'yaml';

import { MAX_SKILL_DESCRIPTION_CHARS } from '../constants';

/**
 * Reading and writing `SKILL.md` in the portable Agent Skills format
 * (https://agentskills.io/specification).
 *
 * The file is the source of truth for a skill: `name` and `description` live in its YAML
 * frontmatter, and every other key an author or another tool put there (`license`,
 * `compatibility`, `metadata`, `allowed-tools`, or anything newer) is carried through a save
 * untouched. That is what lets a skill written here load unmodified elsewhere, and one
 * written elsewhere be imported here without losing fields we do not render.
 *
 * Shared by the editor and the server so both read the same file the same way: the editor
 * shows what the server will store, and the server rejects exactly what the editor flags.
 */

export const SKILL_MARKDOWN_PATH = 'SKILL.md';
export const MAX_SKILL_NAME_CHARS = 64;
export const MAX_SKILL_COMPATIBILITY_CHARS = 500;
/**
 * Frontmatter key that marks a skill as `/` only, the same key Claude Code uses. Every
 * skill in Aline is `/` only, so it is stored and carried through but changes nothing here.
 */
export const DISABLE_MODEL_INVOCATION_KEY = 'disable-model-invocation';

export interface SkillFrontmatter {
  description: string;
  /** Every key besides `name` and `description`, in the order the file had them. */
  extra: Record<string, unknown>;
  name: string;
}

export type ParsedSkillMarkdown =
  | { body: string; frontmatter: SkillFrontmatter; ok: true }
  | { error: string; ok: false };

export interface SkillFrontmatterError {
  field: string;
  message: string;
}

/** What `updateSkillMarkdown` changes; a field left out keeps what the file has. */
export interface SkillMarkdownChanges {
  body?: string;
  description?: string;
  /** Every key besides `name` and `description`. Keys left out are deleted. */
  extra?: Record<string, unknown>;
  name?: string;
}

/**
 * The closing fence has to be a `---` alone on its own line, so a description that merely
 * contains dashes ("the 2024--2025 term") cannot end the block early. The optional group
 * lets an empty block (`---` straight after `---`) parse too.
 */
const FRONTMATTER_PATTERN = /^\uFEFF?---[ \t]*\r?\n(?:([\s\S]*?)\r?\n)?---[ \t]*(?:\r?\n|$)/;
const LEADING_BLANK_LINES_PATTERN = /^(?:[ \t]*\r?\n)*/;
const SKILL_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function parseSkillMarkdown(content: string): ParsedSkillMarkdown {
  const { body, yamlSource } = splitSkillMarkdown(content);
  if (yamlSource === null || !yamlSource.trim())
    return { body, frontmatter: getEmptyFrontmatter(), ok: true };

  const document = parseDocument(yamlSource);
  if (document.errors.length)
    return { error: `The frontmatter is not valid YAML: ${document.errors[0].message}`, ok: false };
  if (!isMap(document.contents))
    return { error: 'The frontmatter must be a list of `key: value` pairs.', ok: false };

  /**
   * `toJS` can still throw on a document that parsed without errors, an undefined alias
   * (`description: *missing`) for one. Report it like any other invalid frontmatter.
   */
  let values: unknown;
  try {
    values = document.toJS();
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return { error: `The frontmatter is not valid YAML: ${reason}`, ok: false };
  }
  if (!getIsRecord(values))
    return { error: 'The frontmatter must be a list of `key: value` pairs.', ok: false };

  const { description, name, ...extra } = values;
  const nameText = getScalarText(name);
  if (nameText === null) return { error: '`name` must be text.', ok: false };
  const descriptionText = getScalarText(description);
  if (descriptionText === null) return { error: '`description` must be text.', ok: false };

  return { body, frontmatter: { description: descriptionText, extra, name: nameText }, ok: true };
}

/**
 * Write a `SKILL.md` back out.
 *
 * A file with nothing in its frontmatter is written as the bare body, so a blank skill stays
 * a blank file instead of gaining an empty `---` block nobody typed.
 */
export function serializeSkillMarkdown({
  body,
  frontmatter,
}: {
  body: string;
  frontmatter: SkillFrontmatter;
}): string {
  const values: Record<string, unknown> = {
    ...(frontmatter.name ? { name: frontmatter.name } : {}),
    ...(frontmatter.description ? { description: frontmatter.description } : {}),
    ...frontmatter.extra,
  };
  if (!Object.keys(values).length) return body;
  // `lineWidth: 0` keeps a long description on one line rather than folding it.
  const yaml = stringify(values, { lineWidth: 0 });
  return `---\n${yaml}---\n${body ? `\n${body}` : ''}`;
}

/**
 * Change parts of a SKILL.md while keeping the rest exactly as written: unknown keys,
 * their formatting and comments, and the body when it is not replaced.
 *
 * The parsed YAML document is edited in place, so an unquoted `version: 1.0` stays `1.0`
 * and comments survive, and a frontmatter none of the changes touch is kept byte for byte.
 * An empty `name` or `description` removes that key. A file without frontmatter gains one
 * only when a key is set. Frontmatter that does not parse cannot be edited in place, so the
 * file is then written from the changes alone with `serializeSkillMarkdown`.
 */
export function updateSkillMarkdown(content: string, changes: SkillMarkdownChanges): string {
  const parsed = parseSkillMarkdown(content);
  const { body, gap, header, yamlSource } = splitSkillMarkdown(content);
  const nextBody = changes.body ?? body;
  if (!parsed.ok)
    return serializeSkillMarkdown({
      body: nextBody,
      frontmatter: {
        description: changes.description ?? '',
        extra: changes.extra ?? {},
        name: changes.name ?? '',
      },
    });

  const { frontmatter } = parsed;
  const next: SkillFrontmatter = {
    description: changes.description ?? frontmatter.description,
    extra: changes.extra ? getDefinedEntries(changes.extra) : frontmatter.extra,
    name: changes.name ?? frontmatter.name,
  };
  if (isEqual(next, frontmatter))
    return `${header}${getHeaderBodySeparator(header, nextBody)}${gap}${nextBody}`;

  const document: Document = yamlSource?.trim() ? parseDocument(yamlSource) : new Document({});
  const map = document.contents;
  // Unreachable: the parse above already refused frontmatter that is not a mapping.
  if (!isMap(map)) return serializeSkillMarkdown({ body: nextBody, frontmatter: next });

  setFrontmatterText({ current: frontmatter.name, document, key: 'name', map, value: next.name });
  setFrontmatterText({
    current: frontmatter.description,
    document,
    key: 'description',
    map,
    value: next.description,
  });
  updateYamlMap({
    current: frontmatter.extra,
    document,
    keep: ['name', 'description'],
    map,
    values: next.extra,
  });

  // Like `serializeSkillMarkdown`, a file left with no keys is written as the bare body.
  if (!map.items.length) return nextBody;
  // `lineWidth: 0` keeps a long description on one line rather than folding it.
  const yaml = document.toString({ lineWidth: 0 });
  const separator = header ? gap : '\n';
  return `---\n${yaml}---\n${nextBody ? separator : ''}${nextBody}`;
}

/**
 * Turn whatever an author typed into a valid skill name: lowercase letters, digits and
 * single hyphens, at most `MAX_SKILL_NAME_CHARS` long ("NDA Review" becomes `nda-review`).
 *
 * `partial` keeps one trailing hyphen, for an input normalizing as the author types: without
 * it, typing the space in "nda review" would be erased before the next word arrives.
 */
export function normalizeSkillName(value: string, { partial = false } = {}): string {
  const name = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, MAX_SKILL_NAME_CHARS);
  return partial ? name : name.replace(/-+$/, '');
}

/**
 * Problems that make a `SKILL.md` invalid under the spec and block saving it.
 *
 * A missing `name` or `description` is not one of them: a skill may be saved half written,
 * and `getIsSkillComplete` is what keeps an unfinished one away from the agent. A value that
 * is present but malformed would be rejected by every other tool that reads the file, so
 * it is refused here too rather than stored and exported broken.
 */
export function getSkillFrontmatterErrors(frontmatter: SkillFrontmatter): SkillFrontmatterError[] {
  const errors: SkillFrontmatterError[] = [];
  const { description, extra, name } = frontmatter;
  if (name && name.length > MAX_SKILL_NAME_CHARS)
    errors.push({
      field: 'name',
      message: `The name must be ${MAX_SKILL_NAME_CHARS} characters or fewer.`,
    });
  else if (name && !SKILL_NAME_PATTERN.test(name))
    errors.push({
      field: 'name',
      message:
        'The name may only contain lowercase letters, numbers and single hyphens, and cannot start or end with a hyphen.',
    });
  if (description.length > MAX_SKILL_DESCRIPTION_CHARS)
    errors.push({
      field: 'description',
      message: `The description must be ${MAX_SKILL_DESCRIPTION_CHARS} characters or fewer.`,
    });

  const { compatibility, license, metadata } = extra;
  const allowedTools = extra['allowed-tools'];
  if (
    compatibility !== undefined &&
    (typeof compatibility !== 'string' ||
      !compatibility.length ||
      compatibility.length > MAX_SKILL_COMPATIBILITY_CHARS)
  )
    errors.push({
      field: 'compatibility',
      message: `\`compatibility\` must be text of 1-${MAX_SKILL_COMPATIBILITY_CHARS} characters.`,
    });
  const disableModelInvocation = extra[DISABLE_MODEL_INVOCATION_KEY];
  if (disableModelInvocation !== undefined && typeof disableModelInvocation !== 'boolean')
    errors.push({
      field: DISABLE_MODEL_INVOCATION_KEY,
      message: `\`${DISABLE_MODEL_INVOCATION_KEY}\` must be \`true\` or \`false\`.`,
    });
  if (license !== undefined && typeof license !== 'string')
    errors.push({ field: 'license', message: '`license` must be text.' });
  if (allowedTools !== undefined && typeof allowedTools !== 'string')
    errors.push({
      field: 'allowed-tools',
      message: '`allowed-tools` must be a space-separated list of tools.',
    });
  /**
   * The spec asks for string values, but `version: 1.0` unquoted reads as a number and is
   * plainly what the author meant, so any scalar passes. A nested list or map does not.
   */
  if (
    metadata !== undefined &&
    (!getIsRecord(metadata) || Object.values(metadata).some(value => getScalarText(value) === null))
  )
    errors.push({
      field: 'metadata',
      message: '`metadata` must map keys to text values.',
    });
  return errors;
}

/**
 * Whether the skill is ready for the agent: a valid name for the `/` picker and a
 * description, which the spec requires of every skill.
 */
export function getIsSkillComplete(frontmatter: SkillFrontmatter): boolean {
  return (
    Boolean(frontmatter.name) &&
    Boolean(frontmatter.description.trim()) &&
    !getSkillFrontmatterErrors(frontmatter).length
  );
}

/** Whether the frontmatter sets `disable-model-invocation: true`. */
export function getSkillDisablesModelInvocation(frontmatter: SkillFrontmatter): boolean {
  return frontmatter.extra[DISABLE_MODEL_INVOCATION_KEY] === true;
}

function getEmptyFrontmatter(): SkillFrontmatter {
  return { description: '', extra: {}, name: '' };
}

/**
 * YAML reads `name: 2024` as a number and `name: yes` as a boolean. Both are text an author
 * meant, so they are kept as the text they were written as; a list or a mapping is not.
 */
function getScalarText(value: unknown): string | null {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function getIsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The parts of a SKILL.md: the frontmatter block with its fences (`header`), the blank lines
 * after it (`gap`) and the body. `yamlSource` is `null` when the file has no frontmatter.
 */
function splitSkillMarkdown(content: string): {
  body: string;
  gap: string;
  header: string;
  yamlSource: string | null;
} {
  const match = FRONTMATTER_PATTERN.exec(content);
  if (!match) return { body: content, gap: '', header: '', yamlSource: null };
  const rest = content.slice(match[0].length);
  const gap = LEADING_BLANK_LINES_PATTERN.exec(rest)?.[0] ?? '';
  return { body: rest.slice(gap.length), gap, header: match[0], yamlSource: match[1] ?? '' };
}

/** The line break a body needs after a header whose closing fence ends the file. */
function getHeaderBodySeparator(header: string, body: string): string {
  if (!header || !body || header.endsWith('\n')) return '';
  return header.includes('\r\n') ? '\r\n' : '\n';
}

/**
 * Set `name` or `description`, or remove it when the new value is empty. A key the file
 * did not have goes where `serializeSkillMarkdown` would put it: name first, then description.
 */
function setFrontmatterText({
  current,
  document,
  key,
  map,
  value,
}: {
  current: string;
  document: Document;
  key: 'description' | 'name';
  map: YAMLMap;
  value: string;
}): void {
  if (value === current) return;
  const pair = findYamlPair(map, key);
  if (!value) {
    if (pair) map.items.splice(map.items.indexOf(pair), 1);
    return;
  }
  if (pair) {
    setYamlPairValue({ document, pair, value });
    return;
  }
  const namePair = key === 'description' ? findYamlPair(map, 'name') : undefined;
  const index = namePair ? map.items.indexOf(namePair) + 1 : 0;
  map.items.splice(index, 0, document.createPair(key, value));
}

/**
 * Make `map` hold `values`, touching only the entries that changed so the others keep their
 * source formatting and comments. A nested map is updated the same way, key by key; any
 * other value that changed is replaced whole. Keys in `keep` are left alone.
 */
function updateYamlMap({
  current,
  document,
  keep = [],
  map,
  values,
}: {
  current: Record<string, unknown>;
  document: Document;
  map: YAMLMap;
  values: Record<string, unknown>;
  keep?: readonly string[];
}): void {
  const keys = new Set(Object.keys(values));
  map.items = map.items.filter(pair => {
    const key = getYamlPairKey(pair);
    return key === null || keep.includes(key) || keys.has(key);
  });
  for (const [key, value] of Object.entries(values)) {
    const currentValue = current[key];
    if (!(key in current) || !isEqual(currentValue, value)) {
      const pair = findYamlPair(map, key);
      if (!pair) map.items.push(document.createPair(key, value));
      else if (isMap(pair.value) && getIsRecord(value) && getIsRecord(currentValue))
        updateYamlMap({ current: currentValue, document, map: pair.value, values: value });
      else setYamlPairValue({ document, pair, value });
    }
  }
}

/** Keeps the existing scalar node when it can, so its style, anchor and comments survive. */
function setYamlPairValue({
  document,
  pair,
  value,
}: {
  document: Document;
  pair: Pair;
  value: unknown;
}): void {
  if (isScalar(pair.value) && getIsYamlScalarValue(value)) pair.value.value = value;
  else pair.value = document.createNode(value);
}

/**
 * Keys are matched as the text `toJS` turns them into, so `1: x` is found as `'1'`. A
 * complex key (a list or a map) has no such text and is never matched.
 */
function findYamlPair(map: YAMLMap, key: string): Pair | undefined {
  return map.items.find(pair => getYamlPairKey(pair) === key);
}

function getYamlPairKey(pair: Pair): string | null {
  return isScalar(pair.key) ? String(pair.key.value) : null;
}

function getIsYamlScalarValue(value: unknown): boolean {
  return value === null || (typeof value !== 'object' && typeof value !== 'function');
}

function getDefinedEntries(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
}
